import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharePointCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const exchangedRef = useRef(false);

  useEffect(() => {
    // Prevent double exchange (React StrictMode fires useEffect twice)
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setError(searchParams.get('error_description') || errorParam);
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received');
      return;
    }

    const exchange = async () => {
      try {
        const redirectUri = `${window.location.origin}/sharepoint-callback`;
        await axios.post(`${API}/sharepoint/oauth/exchange`, {
          code,
          redirect_uri: redirectUri,
        });

        setStatus('success');

        // Notify parent window (if opened as popup)
        if (window.opener) {
          window.opener.postMessage({ type: 'sharepoint-oauth-complete' }, window.location.origin);
          setTimeout(() => window.close(), 500);
        } else {
          // Redirect to chat if opened in same window
          setTimeout(() => navigate('/chat'), 1500);
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Failed to connect SharePoint');
      }
    };

    exchange();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-white">
      <div className="bg-white rounded-xl p-8 shadow-lg max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader className="w-10 h-10 animate-spin text-ugmonk-dark mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">Connecting SharePoint...</h2>
            <p className="text-sm text-gray-500 mt-2">Please wait while we complete the connection.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">SharePoint Connected</h2>
            <p className="text-sm text-gray-500 mt-2">
              {window.opener ? 'This window will close automatically.' : 'Redirecting to chat...'}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Connection Failed</h2>
            <p className="text-sm text-red-500 mt-2">{error}</p>
            <button
              onClick={() => window.opener ? window.close() : navigate('/chat')}
              className="mt-4 px-4 py-2 bg-ugmonk-yellow text-black rounded-lg hover:bg-[#f5cc6a] text-sm"
            >
              {window.opener ? 'Close' : 'Back to Chat'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
